import type { ChapterListStreamInput, ChapterRef } from './types';

export const HORNER_SEQUENCE_SOURCE = 'horner';

export interface HornerReadingList {
  id: number;
  title: string;
  bookIds: number[];
}

export interface HornerReading {
  listId: number;
  listTitle: string;
  bookId: number;
  bookName: string;
  chapter: number;
}

export const HORNER_READING_LISTS: HornerReadingList[] = [
  { id: 1, title: 'Gospels', bookIds: [40, 41, 42, 43] },
  { id: 2, title: 'Pentateuch', bookIds: [1, 2, 3, 4, 5] },
  { id: 3, title: 'Paul and Hebrews', bookIds: [45, 46, 47, 48, 49, 50, 51, 58] },
  {
    id: 4,
    title: 'Letters and Revelation',
    bookIds: [52, 53, 54, 55, 56, 57, 59, 60, 61, 62, 63, 64, 65, 66],
  },
  { id: 5, title: 'Wisdom', bookIds: [18, 21, 22] },
  { id: 6, title: 'Psalms', bookIds: [19] },
  { id: 7, title: 'Proverbs', bookIds: [20] },
  { id: 8, title: 'Old Testament History', bookIds: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17] },
  {
    id: 9,
    title: 'Prophets',
    bookIds: [23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39],
  },
  { id: 10, title: 'Acts', bookIds: [44] },
];

export function resolveHornerReadingsForDay(
  chapterRefs: ChapterRef[],
  dayNumber: number,
): HornerReading[] {
  if (!Number.isInteger(dayNumber) || dayNumber < 1) {
    throw new Error('Horner day number must be a positive integer.');
  }

  const offset = dayNumber - 1;
  return HORNER_READING_LISTS.map((list) => {
    const chapters = chaptersForList(chapterRefs, list);
    if (chapters.length === 0) {
      throw new Error(`No chapters were found for Horner list ${list.id}.`);
    }

    const reference = chapters[offset % chapters.length];
    return {
      listId: list.id,
      listTitle: list.title,
      bookId: reference.bookId,
      bookName: reference.bookName,
      chapter: reference.chapter,
    };
  });
}

export function buildHornerChapterListStreamInput(
  chapterRefs: ChapterRef[],
  dayNumber: number,
): ChapterListStreamInput[] {
  return resolveHornerReadingsForDay(chapterRefs, dayNumber).map((reading) => ({
    bookId: reading.bookId,
    chapter: reading.chapter,
    label: `List ${reading.listId}: ${reading.listTitle}`,
  }));
}

export function hornerReadKey(dayNumber: number, bookId: number, chapter: number): string {
  return `${dayNumber}:${bookId}:${chapter}`;
}

function chaptersForList(chapterRefs: ChapterRef[], list: HornerReadingList): ChapterRef[] {
  const byBook = new Map<number, ChapterRef[]>();

  for (const reference of chapterRefs) {
    if (!list.bookIds.includes(reference.bookId)) {
      continue;
    }

    const chapters = byBook.get(reference.bookId) ?? [];
    chapters.push(reference);
    byBook.set(reference.bookId, chapters);
  }

  return list.bookIds.flatMap((bookId) =>
    (byBook.get(bookId) ?? []).sort((left, right) => left.chapter - right.chapter),
  );
}
